import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchCurrentUser } from '../../ducks/user.duck';

import css from './OrderBreakdown.module.css';

// Surfaces the renter's member-day budget on the customer-side breakdown so
// they can see at a glance how many days they have left after the booking
// they're previewing. Renders only when the breakdown's lineItems contain
// a `line-item/member-discount` (confirming this is a member booking) AND
// the viewer is the customer themselves (we read their membership profile
// from Redux).

const numberFromMaybe = v => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toNumber === 'function') return v.toNumber();
  return Number(v) || 0;
};

const findBookingDays = lineItems => {
  const orderLine = lineItems.find(
    li =>
      (li.code === 'line-item/day' || li.code === 'line-item/night') &&
      !li.reversal
  );
  if (!orderLine) return 0;
  return numberFromMaybe(orderLine.quantity || orderLine.units);
};

const findMemberTier = lineItems => {
  // Customer side has the discount line but the tier is encoded in the
  // provider-side host-offset code; we don't have it cleanly on the customer
  // side. Fall back to "Summer" for the regular tier — last-minute can be
  // detected from the booking length (≤2 days) heuristically.
  const offset = lineItems.find(
    li =>
      typeof li.code === 'string' &&
      /^line-item\/membership-host-offset-(regular|lastMinute)-\d+$/.test(li.code) &&
      !li.reversal
  );
  if (offset) {
    const m = offset.code.match(/^line-item\/membership-host-offset-(regular|lastMinute)-/);
    return m ? m[1] : 'regular';
  }
  return null;
};

const LineItemMemberAllowanceMaybe = props => {
  const { lineItems, isProvider } = props;
  const currentUser = useSelector(state => state.user.currentUser);
  const dispatch = useDispatch();

  // Refetch the current user so daysUsedThisSeason reflects the latest
  // server-side state after a booking applies (the host-side accept hook
  // updates the user's publicData, but Redux doesn't auto-refetch — without
  // this the count stays stale until the user navigates).
  useEffect(() => {
    if (isProvider) return;
    dispatch(fetchCurrentUser());
  }, [dispatch, isProvider]);

  if (isProvider) return null;

  const hasDiscount = lineItems.some(
    li => li.code === 'line-item/member-discount' && !li.reversal
  );
  if (!hasDiscount) return null;

  const profileData = currentUser?.attributes?.profile?.publicData || {};
  // We don't know which tier this booking is using on the customer side
  // (the offset is provider-only). Use the line-item heuristic from the
  // host-offset code if present; otherwise assume regular.
  const tierFromOffset = findMemberTier(lineItems);
  const tierKey =
    tierFromOffset === 'lastMinute' ? 'lastMinuteMembership' : 'membership';
  const membership = profileData[tierKey];
  if (!membership) return null;

  const daysUnlocked = numberFromMaybe(membership.daysUnlocked);
  const daysUsed = numberFromMaybe(membership.daysUsedThisSeason);
  const remaining = Math.max(0, daysUnlocked - daysUsed);
  const bookingDays = findBookingDays(lineItems);
  const tierLabel =
    tierKey === 'lastMinuteMembership' ? 'Last-Minute' : 'Summer';

  return (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        {tierLabel} Membership · {daysUsed} of {daysUnlocked} days used · {remaining} day
        {remaining === 1 ? '' : 's'} remaining
        {bookingDays > 0 ? ` · this booking uses ${bookingDays} day${bookingDays === 1 ? '' : 's'}` : ''}
      </span>
      <span className={css.itemValue} />
    </div>
  );
};

export default LineItemMemberAllowanceMaybe;
